import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  setTestFlightTokenGetter,
  getApps,
  getBuilds,
  getBetaGroups,
  getTesters,
  getUsers,
  inviteTester,
  removeTester,
} from './api';
import './TestFlight.css';

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

const STATUS_MAP = {
  PROCESSING:         { label: 'Processing',        cls: 'tf-status-processing' },
  WAITING_FOR_REVIEW: { label: 'Waiting for Review', cls: 'tf-status-waiting' },
  IN_REVIEW:          { label: 'In Review',          cls: 'tf-status-in-review' },
  APPROVED:           { label: 'Approved',           cls: 'tf-status-approved' },
  REJECTED:           { label: 'Rejected',           cls: 'tf-status-rejected' },
};

function buildStatus(processingState, reviewState) {
  if (processingState === 'PROCESSING') return STATUS_MAP.PROCESSING;
  if (reviewState && STATUS_MAP[reviewState]) return STATUS_MAP[reviewState];
  return { label: 'Ready', cls: 'tf-status-ready' };
}

function TestFlight() {
  const { getToken, isAdmin } = useAuth();

  const [apps, setApps] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState('');
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [testers, setTesters] = useState([]);
  const [builds, setBuilds] = useState([]);
  const [loading, setLoading] = useState({ apps: false, groups: false, testers: false, builds: false });
  const [error, setError] = useState(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviting, setInviting] = useState(false);

  // External group form fields
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirst, setInviteFirst] = useState('');
  const [inviteLast, setInviteLast] = useState('');

  // Internal group picker
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());

  const selectedGroup = groups.find((g) => g.id === selectedGroupId);
  const isInternalGroup = selectedGroup?.isInternal ?? false;

  useEffect(() => {
    setTestFlightTokenGetter(getToken);
  }, [getToken]);

  useEffect(() => {
    setLoading((l) => ({ ...l, apps: true }));
    setError(null);
    getApps()
      .then((res) => setApps(res.data.apps))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading((l) => ({ ...l, apps: false })));
  }, []);

  useEffect(() => {
    if (!selectedAppId) {
      setGroups([]);
      setSelectedGroupId('');
      setTesters([]);
      return;
    }
    setLoading((l) => ({ ...l, groups: true }));
    setGroups([]);
    setSelectedGroupId('');
    setTesters([]);
    getBetaGroups(selectedAppId)
      .then((res) => setGroups(res.data.groups))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading((l) => ({ ...l, groups: false })));
  }, [selectedAppId]);

  const loadTesters = useCallback(() => {
    if (!selectedGroupId) { setTesters([]); return; }
    setLoading((l) => ({ ...l, testers: true }));
    getTesters(selectedGroupId)
      .then((res) => setTesters(res.data.testers))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading((l) => ({ ...l, testers: false })));
  }, [selectedGroupId]);

  useEffect(() => { loadTesters(); }, [loadTesters]);

  useEffect(() => {
    if (!selectedAppId) { setBuilds([]); return; }
    setLoading((l) => ({ ...l, builds: true }));
    getBuilds(selectedAppId)
      .then((res) => setBuilds(res.data.builds))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading((l) => ({ ...l, builds: false })));
  }, [selectedAppId]);

  function loadDevUsers() {
    setLoadingUsers(true);
    getUsers()
      .then((res) => setUsers(res.data.users))
      .catch((err) => setInviteError(err.response?.data?.detail || err.message))
      .finally(() => setLoadingUsers(false));
  }

  function openAddForm() {
    setInviteEmail('');
    setInviteFirst('');
    setInviteLast('');
    setInviteError(null);
    setUserSearch('');
    setSelectedUserIds(new Set());
    setShowAddForm(true);
    if (isInternalGroup) loadDevUsers();
  }

  function closeAddForm() {
    setShowAddForm(false);
    setInviteError(null);
  }

  function toggleUser(id) {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function handleAddExternal(e) {
    e.preventDefault();
    if (!inviteEmail || !inviteFirst || !selectedGroupId) return;
    setInviting(true);
    setInviteError(null);
    try {
      await inviteTester(selectedGroupId, {
        email: inviteEmail,
        firstName: inviteFirst,
        lastName: inviteLast,
      });
      setShowAddForm(false);
      loadTesters();
    } catch (err) {
      setInviteError(err.response?.data?.detail || err.message);
    } finally {
      setInviting(false);
    }
  }

  async function handleAddInternal(e) {
    e.preventDefault();
    if (!selectedUserIds.size || !selectedGroupId) return;
    setInviting(true);
    setInviteError(null);
    try {
      const selected = users.filter((u) => selectedUserIds.has(u.id));
      await Promise.all(
        selected.map((u) =>
          inviteTester(selectedGroupId, {
            email: u.email,
            firstName: u.firstName,
            lastName: u.lastName,
          })
        )
      );
      setShowAddForm(false);
      loadTesters();
    } catch (err) {
      setInviteError(err.response?.data?.detail || err.message);
    } finally {
      setInviting(false);
    }
  }

  async function handleRemove(testerId) {
    try {
      await removeTester(selectedGroupId, testerId);
      setTesters((prev) => prev.filter((t) => t.id !== testerId));
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }

  const testerEmails = new Set(testers.map((t) => t.email.toLowerCase()));

  const filteredUsers = users.filter((u) => {
    if (testerEmails.has(u.email.toLowerCase())) return false;
    const q = userSearch.toLowerCase();
    return (
      !q ||
      u.email.toLowerCase().includes(q) ||
      u.firstName.toLowerCase().includes(q) ||
      u.lastName.toLowerCase().includes(q)
    );
  });

  return (
    <div className="tf-dashboard">
      <div className="tf-header">
        <h1>TestFlight</h1>
        <p>Manage beta testers across your App Store Connect apps</p>
      </div>

      {error && <div className="tf-error">{error}</div>}

      <div className="tf-selectors">
        <div className="tf-field">
          <label htmlFor="tf-app-select">App</label>
          <select
            id="tf-app-select"
            value={selectedAppId}
            onChange={(e) => setSelectedAppId(e.target.value)}
            disabled={loading.apps}
          >
            <option value="">{loading.apps ? 'Loading...' : 'Select an app'}</option>
            {apps.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>

        <div className="tf-field">
          <label htmlFor="tf-group-select">Beta Group</label>
          <select
            id="tf-group-select"
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
            disabled={!selectedAppId || loading.groups}
          >
            <option value="">{loading.groups ? 'Loading...' : 'Select a group'}</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}{g.isInternal ? ' (Internal)' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedAppId && (
        <div className="tf-builds">
          <h2 className="tf-section-title">Recent Builds</h2>
          {loading.builds ? (
            <p className="tf-muted">Loading builds...</p>
          ) : builds.length === 0 ? (
            <p className="tf-muted">No recent builds found.</p>
          ) : (
            <table className="tf-table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Build</th>
                  <th>Uploaded</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {builds.map((b) => {
                  const { label, cls } = buildStatus(b.processingState, b.reviewState);
                  return (
                    <tr key={b.id}>
                      <td>{b.version || '-'}</td>
                      <td><span className="tf-build-num">{b.buildNumber}</span></td>
                      <td>{fmtDate(b.uploadedDate)}</td>
                      <td><span className={`tf-status-badge ${cls}`}>{label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {selectedGroupId && (
        <div className="tf-testers">
          <div className="tf-testers-header">
            <h2 className="tf-testers-title">
              Testers{!loading.testers && ` (${testers.length})`}
            </h2>
            {isAdmin && !showAddForm && (
              <button className="tf-btn-add" onClick={openAddForm}>+ Add Tester</button>
            )}
          </div>

          {showAddForm && isInternalGroup && (
            <form className="tf-add-form" onSubmit={handleAddInternal}>
              {inviteError && <div className="tf-error">{inviteError}</div>}
              <input
                className="tf-user-search"
                type="text"
                placeholder="Search by name or email..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                autoFocus
              />
              <div className="tf-user-list">
                {loadingUsers ? (
                  <p className="tf-muted tf-muted-pad">Loading team members...</p>
                ) : filteredUsers.length === 0 ? (
                  <p className="tf-muted tf-muted-pad">No members match your search.</p>
                ) : (
                  filteredUsers.map((u) => (
                    <label key={u.id} className={`tf-user-item ${selectedUserIds.has(u.id) ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={selectedUserIds.has(u.id)}
                        onChange={() => toggleUser(u.id)}
                      />
                      <span className="tf-user-info">
                        <span className="tf-user-name">{u.firstName} {u.lastName}</span>
                        <span className="tf-user-email">{u.email}</span>
                      </span>
                    </label>
                  ))
                )}
              </div>
              <div className="tf-form-actions">
                <button type="submit" disabled={inviting || !selectedUserIds.size}>
                  {inviting ? 'Adding...' : `Add ${selectedUserIds.size || ''} selected`.trim()}
                </button>
                <button type="button" className="tf-btn-cancel" onClick={closeAddForm}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {showAddForm && !isInternalGroup && (
            <form className="tf-add-form" onSubmit={handleAddExternal}>
              {inviteError && <div className="tf-error">{inviteError}</div>}
              <div className="tf-form-row">
                <input
                  type="text"
                  placeholder="First name *"
                  value={inviteFirst}
                  onChange={(e) => setInviteFirst(e.target.value)}
                  required
                  autoFocus
                />
                <input
                  type="text"
                  placeholder="Last name"
                  value={inviteLast}
                  onChange={(e) => setInviteLast(e.target.value)}
                />
                <input
                  type="email"
                  placeholder="Email *"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                />
                <button type="submit" disabled={inviting || !inviteEmail || !inviteFirst}>
                  {inviting ? 'Adding...' : 'Add'}
                </button>
                <button type="button" className="tf-btn-cancel" onClick={closeAddForm}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {loading.testers ? (
            <p className="tf-muted">Loading testers...</p>
          ) : testers.length === 0 ? (
            <p className="tf-muted">No testers in this group yet.</p>
          ) : (
            <table className="tf-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  {isAdmin && <th></th>}
                </tr>
              </thead>
              <tbody>
                {testers.map((t) => (
                  <tr key={t.id}>
                    <td>
                      {t.inviteType === 'PUBLIC_LINK' && !t.email
                        ? <span className="tf-muted-inline">[Public Link Invite]</span>
                        : t.email}
                    </td>
                    <td>{[t.firstName, t.lastName].filter(Boolean).join(' ') || '-'}</td>
                    {isAdmin && (
                      <td>
                        <button className="tf-btn-remove" onClick={() => handleRemove(t.id)}>
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default TestFlight;
